import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type {
  ThematicNodeContent,
  UpdateThematicNodeContent,
} from '@/types/thematicMaps';

// Lista kolumn do fetchowania — WSZYSTKIE oprócz 4 ciężkich nieużywanych w widoku listy:
//   engine_debug (11MB/mapa — JSONB z forensics), enriched_html, optimized_html, content_html.
// Te 4 nie są czytane przez żadnego konsumenta hooka (tylko zapisywane przy reset).
// final_html + draft_html ZOSTAJĄ — używane do preview + word count w ContentEnginePanel.
// Efekt: payload SELECT ~17MB → ~3.3MB (-80%), eliminuje timeouty podczas bulk batchy.
// Pełną treść (engine_debug etc.) ładuje się osobnym zapytaniem gdy user otworzy artykuł.
const LIST_COLUMNS = [
  'id', 'user_id', 'map_id', 'node_id', 'seo_title', 'meta_description',
  'h2_outline', 'key_points', 'internal_links', 'word_count_target',
  'content_markdown', 'content_status', 'plan_generated_at', 'content_generated_at',
  'plan_model', 'content_model', 'plan_tokens', 'content_tokens', 'created_at',
  'updated_at', 'opening_answer', 'faq', 'do_not_include', 'fan_out', 'outline',
  'distribution', 'draft_html', 'final_html', 'engine_stage', 'engine_started_at',
  'engine_completed_at', 'engine_error', 'engine_cost', 'published_at', 'wp_post_id',
  'wp_post_url', 'quality_review_requested', 'quality_score', 'quality_notes',
  'quality_reviewed_at', 'visibility_checks', 'generated_from_context_hash',
  'current_context_hash', 'is_stale', 'needs_regeneration', 'last_context_checked_at',
  'qa_status', 'qa_checked_at', 'featured_image_url', 'image_prompt', 'image_model',
  'effective_publish_date', 'indexed_at',
].join(', ');

export const useThematicNodeContent = (mapId: string | undefined) => {
  const [contents, setContents] = useState<ThematicNodeContent[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // isBackground=true → fetch wywołany przez realtime subscription (refetch w tle).
  // Wtedy NIE pokazujemy toast'a przy błędzie — za 400ms i tak przyjdzie kolejny refetch.
  // Toast tylko przy pierwszym ładowaniu (mount), gdzie user faktycznie czeka na dane.
  const fetchContents = useCallback(async (isBackground = false) => {
    if (!user || !mapId) return;

    try {
      if (!isBackground) setLoading(true);
      const { data, error } = await (supabase as any)
        .from('thematic_node_content')
        .select(LIST_COLUMNS)
        .eq('map_id', mapId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Pass through ALL columns from DB — no manual mapping needed
      // This ensures new columns (outline, engine_stage, qa_status etc.) are available
      const mapped: ThematicNodeContent[] = (data || []).map((r: any) => ({
        ...r,
        // Ensure defaults for optional fields
        h2_outline: r.h2_outline || [],
        key_points: r.key_points || [],
        faq: r.faq || [],
        do_not_include: r.do_not_include || [],
        internal_links: r.internal_links || [],
        word_count_target: r.word_count_target || 1500,
        plan_tokens: r.plan_tokens || 0,
        content_tokens: r.content_tokens || 0,
        opening_answer: r.opening_answer || null,
      }));

      setContents(mapped);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania treści';
      if (isBackground) {
        // Refetch w tle (realtime) — nie zawracaj user'owi głowy toast'em.
        // Następny refetch za chwilę naprawi widok. Loguj tylko do konsoli.
        console.warn('[useThematicNodeContent] background refetch failed (non-blocking):', msg);
      } else {
        toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      }
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createContentsBulk = useCallback(async (
    nodeIds: string[]
  ): Promise<ThematicNodeContent[]> => {
    if (!user || !mapId || nodeIds.length === 0) return [];

    try {
      const rows = nodeIds.map(nodeId => ({
        user_id: user.id,
        map_id: mapId,
        node_id: nodeId,
        content_status: 'empty',
      }));

      const { data, error } = await (supabase as any)
        .from('thematic_node_content')
        .insert(rows)
        .select();

      if (error) throw error;

      await fetchContents();
      return data as ThematicNodeContent[];
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia rekordów treści';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return [];
    }
  }, [user, mapId, toast, fetchContents]);

  const updateContent = useCallback(async (
    id: string,
    updates: UpdateThematicNodeContent
  ): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_node_content')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      setContents(prev => prev.map(c =>
        c.id === id ? { ...c, ...updates, updated_at: new Date().toISOString() } : c
      ));
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji treści';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const upsertContent = useCallback(async (
    nodeId: string,
    updates: UpdateThematicNodeContent
  ): Promise<ThematicNodeContent | null> => {
    if (!user || !mapId) return null;

    try {
      const existing = contents.find(c => c.node_id === nodeId);

      if (existing) {
        const { error } = await (supabase as any)
          .from('thematic_node_content')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', existing.id);

        if (error) throw error;

        const updated = { ...existing, ...updates, updated_at: new Date().toISOString() };
        setContents(prev => prev.map(c => c.id === existing.id ? updated : c));
        return updated as ThematicNodeContent;
      } else {
        const { data, error } = await (supabase as any)
          .from('thematic_node_content')
          .insert({
            user_id: user.id,
            map_id: mapId,
            node_id: nodeId,
            content_status: 'empty',
            ...updates,
          })
          .select()
          .single();

        if (error) throw error;

        await fetchContents();
        return data as ThematicNodeContent;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd upsert treści';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, mapId, contents, toast, fetchContents]);

  const deleteContent = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_node_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setContents(prev => prev.filter(c => c.id !== id));
      toast({ title: 'Sukces', description: 'Treść usunięta' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania treści';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchContents();
    }
  }, [user?.id, mapId, fetchContents]);

  const { isLive } = useRealtimeSubscription({
    channel: `thematic_node_content_${mapId || 'none'}`,
    table: 'thematic_node_content',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: () => fetchContents(true), // background refetch — silent on error
  });

  return {
    contents,
    loading,
    isLive,
    createContentsBulk,
    updateContent,
    upsertContent,
    deleteContent,
    refreshContents: fetchContents,
  };
};
